import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { communityRegions } from '~/api/community/constants';
import { jobPostAbroadRegion, jobPostExperienceLevels, jobPostLocationTypes, jobPostPriceUnits } from '~/api/jobPosts/constants';
import { Activity, availablePrimaryActivities } from '~/constants/constants';
import { useIntl } from '~/hooks/useIntl';

export const useRegionOptions = () => {
  const { compareForSort } = useIntl();
  const { t } = useTranslation(['community']);

  return useMemo(
    () => [
      ...communityRegions
        .map((region) => ({ value: region, label: t(`community:regions.${region}`) }))
        .sort(({ label: a }, { label: b }) => compareForSort(a, b)),
      { value: jobPostAbroadRegion, label: t(`community:regions.Abroad`) },
    ],
    [compareForSort, t],
  );
};

export const useActivityOptions = () => {
  const { compareForSort } = useIntl();
  const { t } = useTranslation(['common', 'community']);

  return useMemo(
    () => [
      ...availablePrimaryActivities
        .map((activity) => ({ value: activity, label: t(`common:activities.${activity}.label`) }))
        .sort(({ label: a }, { label: b }) => compareForSort(a, b)),
      { value: Activity.other, label: t(`common:activities.${Activity.other}.label`) },
    ],
    [compareForSort, t],
  );
};

export const useLocationTypeOptions = () => {
  const { t } = useTranslation(['jobPosts']);

  return useMemo(
    () =>
      jobPostLocationTypes.map((locationType) => ({
        value: locationType,
        label: t(`jobPosts:fields.locationType.options.${locationType}`),
      })),
    [t],
  );
};

export const usePriceUnitOptions = () => {
  const { t } = useTranslation(['jobPosts']);

  return useMemo(
    () => jobPostPriceUnits.map((priceType) => ({ value: priceType, label: t(`jobPosts:fields.priceUnit.options.${priceType}`) })),
    [t],
  );
};

export const useExperienceLevelOptions = () => {
  const { t } = useTranslation(['jobPosts']);

  return useMemo(
    () =>
      jobPostExperienceLevels.map((experienceLevel) => ({
        value: experienceLevel,
        label: t(`jobPosts:fields.experienceLevel.options.${experienceLevel}`),
      })),
    [t],
  );
};
